import { updateProfile } from '../containers/ProfileSettingsPage/ProfileSettingsPage.duck';
import { updateSubscriptionType } from '../containers/SubscriptionPage/SubscriptionPage.duck';
import {
  cancelListingSubscription,
  cancelSubscription,
  createChatPurchaseIntent,
  createPaymentIntentAPI,
  createStripeSubscriptionForListing,
  createSubscriptionPrice,
  deleteSubscriptionPrice,
  fetchSubscriptionPrice,
  initiateStripeSubscription,
  updateSubscriptionPlan,
} from '../util/api';
import { StatusCode, TRANSACTION_PROCESS_ALIAS, Transitions } from '../util/enums';
import { storableError } from '../util/errors';
import { updateUnlockablePurchases } from '../util/genericHelpers';
import * as log from '../util/log';
import { showErrorToast, showSuccessToast } from '../util/toast';
import { getUserSubscriptionLookupkeys } from '../util/userDataExtraction';
import { fetchCurrentUser } from './user.duck';

// https://stripe.com/docs/api/payment_intents/object#payment_intent_object-status
const STRIPE_PI_HAS_PASSED_CONFIRM = ['processing', 'requires_capture', 'canceled', 'succeeded'];

// ================ Action types ================ //

export const STRIPE_ACCOUNT_CLEAR_ERROR = 'app/stripe/STRIPE_ACCOUNT_CLEAR_ERROR';

export const ACCOUNT_OPENER_CREATE_REQUEST = 'app/stripe/ACCOUNT_OPENER_CREATE_REQUEST';
export const ACCOUNT_OPENER_CREATE_SUCCESS = 'app/stripe/ACCOUNT_OPENER_CREATE_SUCCESS';
export const ACCOUNT_OPENER_CREATE_ERROR = 'app/stripe/ACCOUNT_OPENER_CREATE_ERROR';

export const CREATE_SUBSCRIPTION_PRICE_REQUEST = 'app/stripe/CREATE_SUBSCRIPTION_PRICE_REQUEST';
export const CREATE_SUBSCRIPTION_PRICE_SUCCESS = 'app/stripe/CREATE_SUBSCRIPTION_PRICE_SUCCESS';
export const CREATE_SUBSCRIPTION_PRICE_ERROR = 'app/stripe/CREATE_SUBSCRIPTION_PRICE_ERROR';

export const PERSON_CREATE_REQUEST = 'app/stripe/PERSON_CREATE_REQUEST';
export const PERSON_CREATE_SUCCESS = 'app/stripe/PERSON_CREATE_SUCCESS';
export const PERSON_CREATE_ERROR = 'app/stripe/PERSON_CREATE_ERROR';

export const CLEAR_PAYMENT_TOKEN = 'app/stripe/CLEAR_PAYMENT_TOKEN';

export const HANDLE_CARD_PAYMENT_REQUEST = 'app/stripe/HANDLE_CARD_PAYMENT_REQUEST';
export const HANDLE_CARD_PAYMENT_SUCCESS = 'app/stripe/HANDLE_CARD_PAYMENT_SUCCESS';
export const HANDLE_CARD_PAYMENT_ERROR = 'app/stripe/HANDLE_CARD_PAYMENT_ERROR';

export const HANDLE_CARD_SETUP_REQUEST = 'app/stripe/HANDLE_CARD_SETUP_REQUEST';
export const HANDLE_CARD_SETUP_SUCCESS = 'app/stripe/HANDLE_CARD_SETUP_SUCCESS';
export const HANDLE_CARD_SETUP_ERROR = 'app/stripe/HANDLE_CARD_SETUP_ERROR';

export const CLEAR_HANDLE_CARD_PAYMENT = 'app/stripe/CLEAR_HANDLE_CARD_PAYMENT';

export const RETRIEVE_PAYMENT_INTENT_REQUEST = 'app/stripe/RETRIEVE_PAYMENT_INTENT_REQUEST';
export const RETRIEVE_PAYMENT_INTENT_SUCCESS = 'app/stripe/RETRIEVE_PAYMENT_INTENT_SUCCESS';
export const RETRIEVE_PAYMENT_INTENT_ERROR = 'app/stripe/RETRIEVE_PAYMENT_INTENT_ERROR';

export const RETRIEVE_SUBSCRIPTION_PRICE_REQUEST = 'app/stripe/RETRIEVE_SUBSCRIPTION_PRICE_REQUEST';
export const RETRIEVE_SUBSCRIPTION_PRICE_SUCCESS = 'app/stripe/RETRIEVE_SUBSCRIPTION_PRICE_SUCCESS';
export const RETRIEVE_SUBSCRIPTION_PRICE_ERROR = 'app/stripe/RETRIEVE_SUBSCRIPTION_PRICE_ERROR';
export const CLEAR_SUBSCRIPTION_PRICES = 'app/stripe/CLEAR_SUBSCRIPTION_PRICES';

export const CANCEL_SUBSCRIPTION_REQUEST = 'app/stripe/CANCEL_SUBSCRIPTION_REQUEST';
export const CANCEL_SUBSCRIPTION_SUCCESS = 'app/stripe/CANCEL_SUBSCRIPTION_SUCCESS';
export const CANCEL_SUBSCRIPTION_ERROR = 'app/stripe/CANCEL_SUBSCRIPTION_ERROR';

export const DELETE_SUBSCRIPTION_PRICE_REQUEST = 'app/stripe/DELETE_SUBSCRIPTION_PRICE_REQUEST';
export const DELETE_SUBSCRIPTION_PRICE_SUCCESS = 'app/stripe/DELETE_SUBSCRIPTION_PRICE_SUCCESS';
export const DELETE_SUBSCRIPTION_PRICE_ERROR = 'app/stripe/DELETE_SUBSCRIPTION_PRICE_ERROR';

export const CREATE_PAYMENT_INTENT_REQUEST = 'app/stripe/CREATE_PAYMENT_INTENT_REQUEST';
export const CREATE_PAYMENT_INTENT_SUCCESS = 'app/stripe/CREATE_PAYMENT_INTENT_SUCCESS';
export const CREATE_PAYMENT_INTENT_FAILURE = 'app/stripe/CREATE_PAYMENT_INTENT_FAILURE';

export const CANCEL_LISTING_SUBSCRIPTION_REQUEST = 'app/stripe/CANCEL_LISTING_SUBSCRIPTION_REQUEST';
export const CANCEL_LISTING_SUBSCRIPTION_SUCCESS = 'app/stripe/CANCEL_LISTING_SUBSCRIPTION_SUCCESS';
export const CANCEL_LISTING_SUBSCRIPTION_ERROR = 'app/stripe/CANCEL_LISTING_SUBSCRIPTION_ERROR';

// ================ Reducer ================ //

const initialState = {
  confirmCardPaymentInProgress: false,
  confirmCardPaymentError: null,
  handleCardSetupInProgress: false,
  handleCardSetupError: null,
  paymentIntent: null,
  setupIntent: null,
  retrievePaymentIntentInProgress: false,
  retrievePaymentIntentError: null,
  subscriptionPrices: null,
  retrieveSubscriptionPriceInProgress: false,
  retrieveSubscriptionPriceError: null,
  cancelSubscriptionInProgress: false,
  cancelSubscriptionError: null,
  createSubscriptionPriceInProgress: false,
  createSubscriptionPriceError: null,
  paymentLoading: false,
  paymentData: null,
  paymentError: null,
  cancelListingSubscriptionInProgress: false,
  cancelListingSubscriptionError: null,
  currentSubscriptionId: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case STRIPE_ACCOUNT_CLEAR_ERROR:
      return { ...initialState };

    case ACCOUNT_OPENER_CREATE_REQUEST:
      return {
        ...state,
        createAccountOpenerError: null,
        createAccountOpenerInProgress: true,
      };
    case ACCOUNT_OPENER_CREATE_SUCCESS:
      return { ...state, createAccountOpenerInProgress: false, personAccountOpener: payload };
    case ACCOUNT_OPENER_CREATE_ERROR:
      console.error(payload);
      return { ...state, createAccountOpenerError: payload, createAccountOpenerInProgress: false };

    case PERSON_CREATE_REQUEST:
      return {
        ...state,
        persons: [
          ...state.persons,
          {
            ...payload,
            createStripePersonError: null,
            createStripePersonInProgress: true,
          },
        ],
      };
    case PERSON_CREATE_SUCCESS:
      return {
        ...state,
        persons: state.persons.map(p => {
          return p.personToken === payload.personToken
            ? { ...payload, createStripePersonInProgress: false }
            : p;
        }),
      };
    case PERSON_CREATE_ERROR:
      console.error(payload);
      return {
        ...state,
        persons: state.persons.map(p => {
          return p.personToken === payload.personToken
            ? { ...p, createStripePersonInProgress: false, createStripePersonError: payload.error }
            : p;
        }),
      };

    case HANDLE_CARD_PAYMENT_REQUEST:
      return {
        ...state,
        confirmCardPaymentError: null,
        confirmCardPaymentInProgress: true,
      };
    case HANDLE_CARD_PAYMENT_SUCCESS:
      return { ...state, paymentIntent: payload, confirmCardPaymentInProgress: false };
    case HANDLE_CARD_PAYMENT_ERROR:
      console.error(payload);
      return { ...state, confirmCardPaymentError: payload, confirmCardPaymentInProgress: false };

    case HANDLE_CARD_SETUP_REQUEST:
      return {
        ...state,
        handleCardSetupError: null,
        handleCardSetupInProgress: true,
      };
    case HANDLE_CARD_SETUP_SUCCESS:
      return { ...state, setupIntent: payload, handleCardSetupInProgress: false };
    case HANDLE_CARD_SETUP_ERROR:
      console.error(payload);
      return { ...state, handleCardSetupError: payload, handleCardSetupInProgress: false };

    case CLEAR_HANDLE_CARD_PAYMENT:
      return {
        ...state,
        confirmCardPaymentInProgress: false,
        confirmCardPaymentError: null,
        paymentIntent: null,
      };

    case RETRIEVE_PAYMENT_INTENT_REQUEST:
      return {
        ...state,
        retrievePaymentIntentError: null,
        retrievePaymentIntentInProgress: true,
      };
    case RETRIEVE_PAYMENT_INTENT_SUCCESS:
      return { ...state, paymentIntent: payload, retrievePaymentIntentInProgress: false };
    case RETRIEVE_PAYMENT_INTENT_ERROR:
      console.error(payload);
      return {
        ...state,
        retrievePaymentIntentError: payload,
        retrievePaymentIntentInProgress: false,
      };

    case RETRIEVE_SUBSCRIPTION_PRICE_REQUEST:
      return {
        ...state,
        subscriptionPrices: null,
        retrieveSubscriptionPriceInProgress: true,
        retrieveSubscriptionPriceError: null,
      };
    case RETRIEVE_SUBSCRIPTION_PRICE_SUCCESS:
      return {
        ...state,
        subscriptionPrices: payload,
        retrieveSubscriptionPriceInProgress: false,
        retrieveSubscriptionPriceError: null,
      };
    case RETRIEVE_SUBSCRIPTION_PRICE_ERROR:
      return {
        ...state,
        subscriptionPrices: null,
        retrieveSubscriptionPriceInProgress: false,
        retrieveSubscriptionPriceError: payload,
      };
    case CLEAR_SUBSCRIPTION_PRICES:
      return {
        ...state,
        subscriptionPrices: null,
      };
    case CANCEL_SUBSCRIPTION_REQUEST:
      return {
        ...state,
        cancelSubscriptionInProgress: true,
        cancelSubscriptionError: null,
      };
    case CANCEL_SUBSCRIPTION_SUCCESS:
      return {
        ...state,
        cancelSubscriptionInProgress: false,
        cancelSubscriptionError: null,
      };
    case CANCEL_SUBSCRIPTION_ERROR:
      return {
        ...state,
        cancelSubscriptionInProgress: false,
        cancelSubscriptionError: payload,
      };
    case CREATE_SUBSCRIPTION_PRICE_REQUEST:
      return {
        ...state,
        createSubscriptionPriceInProgress: true,
        createSubscriptionPriceError: null,
      };
    case CREATE_SUBSCRIPTION_PRICE_SUCCESS:
      return {
        ...state,
        createSubscriptionPriceInProgress: false,
        createSubscriptionPriceError: null,
      };
    case CREATE_SUBSCRIPTION_PRICE_ERROR:
      return {
        ...state,
        createSubscriptionPriceInProgress: false,
        createSubscriptionPriceError: payload,
      };
    case CREATE_PAYMENT_INTENT_REQUEST:
      return { ...state, paymentLoading: true, paymentError: null };
    case CREATE_PAYMENT_INTENT_SUCCESS:
      return { ...state, paymentLoading: false, paymentData: action.payload, paymentError: null };
    case CREATE_PAYMENT_INTENT_FAILURE:
      return { ...state, paymentLoading: false, paymentError: action.payload };

    case CANCEL_LISTING_SUBSCRIPTION_REQUEST:
      return {
        ...state,
        cancelListingSubscriptionInProgress: true,
        cancelListingSubscriptionError: null,
        currentSubscriptionId: payload,
      };
    case CANCEL_LISTING_SUBSCRIPTION_SUCCESS:
      return {
        ...state,
        cancelListingSubscriptionInProgress: false,
        cancelListingSubscriptionError: null,
        currentSubscriptionId: null,
      };
    case CANCEL_LISTING_SUBSCRIPTION_ERROR:
      return {
        ...state,
        cancelListingSubscriptionInProgress: false,
        currentSubscriptionId: null,
        cancelListingSubscriptionError: payload,
      };
    default:
      return state;
  }
}

// ================ Action creators ================ //

export const stripeAccountClearError = () => ({
  type: STRIPE_ACCOUNT_CLEAR_ERROR,
});

export const confirmCardPaymentRequest = () => ({
  type: HANDLE_CARD_PAYMENT_REQUEST,
});

export const confirmCardPaymentSuccess = payload => ({
  type: HANDLE_CARD_PAYMENT_SUCCESS,
  payload,
});

export const confirmCardPaymentError = payload => ({
  type: HANDLE_CARD_PAYMENT_ERROR,
  payload,
  error: true,
});

export const deleteSubscriptionPriceRequest = () => ({
  type: DELETE_SUBSCRIPTION_PRICE_REQUEST,
});

export const deleteSubscriptionPriceSuccess = () => ({
  type: DELETE_SUBSCRIPTION_PRICE_SUCCESS,
});

export const deleteSubscriptionPriceError = error => ({
  type: DELETE_SUBSCRIPTION_PRICE_ERROR,
  payload: error,
  error: true,
});

export const handleCardSetupRequest = () => ({
  type: HANDLE_CARD_SETUP_REQUEST,
});

export const handleCardSetupSuccess = payload => ({
  type: HANDLE_CARD_SETUP_SUCCESS,
  payload,
});

export const handleCardSetupError = payload => ({
  type: HANDLE_CARD_SETUP_ERROR,
  payload,
  error: true,
});

export const initializeCardPaymentData = () => ({
  type: CLEAR_HANDLE_CARD_PAYMENT,
});

export const retrievePaymentIntentRequest = () => ({
  type: RETRIEVE_PAYMENT_INTENT_REQUEST,
});

export const retrievePaymentIntentSuccess = payload => ({
  type: RETRIEVE_PAYMENT_INTENT_SUCCESS,
  payload,
});

export const retrievePaymentIntentError = payload => ({
  type: RETRIEVE_PAYMENT_INTENT_ERROR,
  payload,
  error: true,
});

export const retrieveSubscriptionPriceRequest = () => ({
  type: RETRIEVE_SUBSCRIPTION_PRICE_REQUEST,
});
export const retrieveSubscriptionPriceSuccess = data => ({
  type: RETRIEVE_SUBSCRIPTION_PRICE_SUCCESS,
  payload: data,
});
export const retrieveSubscriptionPriceError = error => ({
  type: RETRIEVE_SUBSCRIPTION_PRICE_ERROR,
  payload: error,
  error: true,
});
export const clearSubscriptionPrices = () => ({
  type: CLEAR_SUBSCRIPTION_PRICES,
});
export const cancelSubscriptionRequest = () => ({
  type: CANCEL_SUBSCRIPTION_REQUEST,
});
export const cancelSubscriptionSuccess = () => ({
  type: CANCEL_SUBSCRIPTION_SUCCESS,
});
export const cancelSubscriptionError = error => ({
  type: CANCEL_SUBSCRIPTION_ERROR,
  payload: error,
  error: true,
});
export const createSubscriptionPriceRequest = () => ({
  type: CREATE_SUBSCRIPTION_PRICE_REQUEST,
});
export const createSubscriptionPriceSuccess = () => ({
  type: CREATE_SUBSCRIPTION_PRICE_SUCCESS,
});
export const createSubscriptionPriceError = error => ({
  type: CREATE_SUBSCRIPTION_PRICE_ERROR,
  payload: error,
  error: true,
});

const createPaymentIntentRequest = () => ({ type: CREATE_PAYMENT_INTENT_REQUEST });
const createPaymentIntentSuccess = data => ({ type: CREATE_PAYMENT_INTENT_SUCCESS, payload: data });
const createPaymentIntentFailure = error => ({
  type: CREATE_PAYMENT_INTENT_FAILURE,
  payload: error,
});

export const cancelListingSubscriptionRequest = subscriptionId => ({
  type: CANCEL_LISTING_SUBSCRIPTION_REQUEST,
  payload: subscriptionId,
});
export const cancelListingSubscriptionSuccess = () => ({
  type: CANCEL_LISTING_SUBSCRIPTION_SUCCESS,
});
export const cancelListingSubscriptionError = error => ({
  type: CANCEL_LISTING_SUBSCRIPTION_ERROR,
  payload: error,
  error: true,
});

// ================ Selectors ================ //

export const subscriptionPlanSelector = state => {
  const {
    subscriptionPrices,
    retrieveSubscriptionPriceInProgress,
    retrieveSubscriptionPriceError,
  } = state.stripe;
  return {
    subscriptionPrices,
    retrieveSubscriptionPriceInProgress,
    retrieveSubscriptionPriceError,
  };
};

export const handleCardSetupErrorSelector = state => {
  return state.stripe.handleCardSetupError;
};
export const cancelSubscriptionSelector = state => {
  const { cancelSubscriptionInProgress, cancelSubscriptionError } = state.stripe;
  return {
    cancelSubscriptionInProgress,
    cancelSubscriptionError,
  };
};

export const createSubscriptionPlanSelector = state => {
  const { createSubscriptionPriceInProgress, createSubscriptionPriceError } = state.stripe;
  return {
    createSubscriptionPriceInProgress,
    createSubscriptionPriceError,
  };
};
export const isCancelingListingSubSelector = state =>
  state.stripe.cancelListingSubscriptionInProgress;

export const currentSubscriptionIdSelector = state => state.stripe.currentSubscriptionId;

// ================ Thunks ================ //

export const retrievePaymentIntent = params => dispatch => {
  const { stripe, stripePaymentIntentClientSecret } = params;
  dispatch(retrievePaymentIntentRequest());

  return stripe
    .retrievePaymentIntent(stripePaymentIntentClientSecret)
    .then(response => {
      if (response.error) {
        return Promise.reject(response);
      } else {
        dispatch(retrievePaymentIntentSuccess(response.paymentIntent));
        return response;
      }
    })
    .catch(err => {
      // Unwrap Stripe error.
      const e = err.error || storableError(err);
      dispatch(retrievePaymentIntentError(e));

      // Log error
      const { code, doc_url, message, payment_intent } = err.error || {};
      const loggableError = err.error
        ? {
            code,
            message,
            doc_url,
            paymentIntentStatus: payment_intent
              ? payment_intent.status
              : 'no payment_intent included',
          }
        : e;
      log.error(loggableError, 'stripe-retrieve-payment-intent-failed', {
        stripeMessage: loggableError.message,
      });
      throw err;
    });
};

export const confirmCardPayment = params => dispatch => {
  // It's required to use the same instance of Stripe as where the card has been created
  // so that's why Stripe needs to be passed here and we can't create a new instance.
  const { stripe, paymentParams, stripePaymentIntentClientSecret } = params;
  const transactionId = params.orderId;

  dispatch(confirmCardPaymentRequest());

  // When using default payment method paymentParams.payment_method is
  // already set Marketplace API side, when request-payment transition is made
  // so there's no need for paymentParams
  const args = paymentParams
    ? [stripePaymentIntentClientSecret, paymentParams]
    : [stripePaymentIntentClientSecret];

  const doConfirmCardPayment = () =>
    stripe.confirmCardPayment(...args).then(response => {
      if (response.error) {
        return Promise.reject(response);
      } else {
        dispatch(confirmCardPaymentSuccess(response));
        return { ...response, transactionId };
      }
    });

  // First, check if the payment intent has already been confirmed and it just requires capture.
  return stripe
    .retrievePaymentIntent(stripePaymentIntentClientSecret)
    .then(response => {
      // Handle response.error or response.paymentIntent
      if (response.error) {
        return Promise.reject(response);
      } else if (STRIPE_PI_HAS_PASSED_CONFIRM.includes(response?.paymentIntent?.status)) {
        // Payment Intent has been confirmed already, move forward.
        dispatch(confirmCardPaymentSuccess(response));
        return { ...response, transactionId };
      } else {
        // If payment intent has not been confirmed yet, confirm it.
        return doConfirmCardPayment();
      }
    })
    .catch(err => {
      // Unwrap Stripe error.
      const e = err.error || storableError(err);
      dispatch(confirmCardPaymentError(e));

      // Log error
      const containsPaymentIntent = err.error && err.error.payment_intent;
      const { code, doc_url, message, payment_intent } = containsPaymentIntent ? err.error : {};
      const loggableError = containsPaymentIntent
        ? {
            code,
            message,
            doc_url,
            paymentIntentStatus: payment_intent.status,
          }
        : e;
      log.error(loggableError, 'stripe-handle-card-payment-failed', {
        stripeMessage: loggableError.message,
      });
      throw e;
    });
};

export const handleCardSetup = params => dispatch => {
  // It's required to use the same instance of Stripe as where the card has been created
  // so that's why Stripe needs to be passed here and we can't create a new instance.
  const { stripe, card, setupIntentClientSecret, paymentParams } = params;

  dispatch(handleCardSetupRequest());

  return stripe
    .handleCardSetup(setupIntentClientSecret, card, paymentParams)
    .then(response => {
      if (response.error) {
        return Promise.reject(response);
      } else {
        dispatch(handleCardSetupSuccess(response));
        return response;
      }
    })
    .catch(err => {
      // Unwrap Stripe error.
      const e = err.error || storableError(err);
      dispatch(handleCardSetupError(e));

      // Log error
      const containsSetupIntent = err.error && err.error.setup_intent;
      const { code, doc_url, message, setup_intent } = containsSetupIntent ? err.error : {};
      const loggableError = containsSetupIntent
        ? {
            code,
            message,
            doc_url,
            paymentIntentStatus: setup_intent.status,
          }
        : e;
      log.error(loggableError, 'stripe-handle-card-setup-failed', {
        stripeMessage: loggableError.message,
      });
      throw e;
    });
};

export const retrieveSubscriptionPrice = params => async (dispatch, getState, sdk) => {
  dispatch(retrieveSubscriptionPriceRequest());
  try {
    const response = await fetchSubscriptionPrice({ ...params });
    if (response.statusCode === StatusCode.SUCCESS) {
      dispatch(retrieveSubscriptionPriceSuccess(response.data?.productPrices));
    }
  } catch (e) {
    // showErrorToast('Error while fetching subscription price');
    dispatch(retrieveSubscriptionPriceError(storableError(e)));
  }
};

export const initiateSubscription = params => async (dispatch, getState, sdk) => {
  try {
    const response = await initiateStripeSubscription(params);
    if (response.statusCode === StatusCode.CREATED) {
      dispatch(fetchCurrentUser());
      showSuccessToast('Successfully created subscription');
    }
  } catch (error) {
    showErrorToast('Error while creating subscription');
    throw new Error(error.message);
  }
};
export const createSubscriptionForListing = params => async (dispatch, getState, sdk) => {
  try {
    const response = await createStripeSubscriptionForListing(params);
    if (response.statusCode === StatusCode.CREATED) {
      dispatch(fetchCurrentUser());
      showSuccessToast('Successfully created subscription');
    }
  } catch (error) {
    showErrorToast('Error while creating subscription');
    throw new Error(error.message);
  }
};
export const discontinueSubscription = params => async (dispatch, getState, sdk) => {
  dispatch(cancelSubscriptionRequest());
  try {
    const response = await cancelSubscription(params);
    if (response.statusCode === StatusCode.SUCCESS) {
      dispatch(fetchCurrentUser());
      dispatch(cancelSubscriptionSuccess());

      showSuccessToast('Successfully cancelled subscription');
    }
  } catch (error) {
    showErrorToast('Error while cancelling subscription');
    dispatch(cancelSubscriptionError(storableError(error)));

    throw new Error(error.message);
  }
};

export const updateSubscription = params => async (dispatch, getState, sdk) => {
  const { subscriptionType, authorId, ...rest } = params;
  try {
    const response = await updateSubscriptionPlan(rest);
    if (response.statusCode === StatusCode.SUCCESS) {
      dispatch(
        updateSubscriptionType({
          subscriptionType,
          authorId,
        })
      );
      dispatch(fetchCurrentUser());
      showSuccessToast('Successfully updated subscription');
    }
  } catch (error) {
    showErrorToast('Error while updating subscription');
    throw new Error(error.message);
  }
};
export const createAuthorSubscriptionPrice = params => async (dispatch, getState, sdk) => {
  dispatch(createSubscriptionPriceRequest());
  try {
    const response = await createSubscriptionPrice(params);
    if (response.statusCode === StatusCode.CREATED) {
      showSuccessToast('Successfully Created Subscription Plan');
      dispatch(createSubscriptionPriceSuccess());
      const currentUser = await dispatch(fetchCurrentUser());
      dispatch(retrieveSubscriptionPriceSuccess(response.data?.productPrices));
      const lookupKeys = getUserSubscriptionLookupkeys(currentUser);
      await dispatch(
        retrieveSubscriptionPrice({
          lookupKeys,
        })
      );
    }
  } catch (error) {
    showErrorToast('Error while creating subscription plan');
    dispatch(createSubscriptionPriceError(storableError(error)));
  }
};
export const deleteStripeSubscriptionPrice = params => async (dispatch, getState, sdk) => {
  dispatch(deleteSubscriptionPriceRequest());

  try {
    const { lookupKeys = [], ...rest } = params || {};
    const response = await deleteSubscriptionPrice(rest);

    if (response.statusCode === StatusCode.SUCCESS) {
      dispatch(deleteSubscriptionPriceSuccess());
      await sdk.currentUser.updateProfile({
        publicData: { lookupKeys },
      });
      showSuccessToast('Successfully deleted subscription price');
    }
  } catch (error) {
    showErrorToast('Error while deleting subscription price');
    dispatch(deleteSubscriptionPriceError(storableError(error)));
    throw error; // Re-throw original error to preserve stack trace
  } finally {
    // Refresh user data to reflect the updated subscription state
    dispatch(fetchCurrentUser());
  }
};

export const createStripePaymentIntent = (body, params = {}) => async dispatch => {
  dispatch(createPaymentIntentRequest());

  const currentUser = await dispatch(fetchCurrentUser());

  try {
    const { paymentIntentId } = await createPaymentIntentAPI(body);
    dispatch(createPaymentIntentSuccess(paymentIntentId));

    const existingPurchases =
      currentUser?.attributes?.profile?.protectedData?.unlockedPurchases || [];
    const updatedPurchases = updateUnlockablePurchases(existingPurchases, params, paymentIntentId);

    if (paymentIntentId) {
      await dispatch(
        updateProfile({
          protectedData: { unlockedPurchases: updatedPurchases },
        })
      );
    }
  } catch (error) {
    dispatch(createPaymentIntentFailure(error.message));
    console.error('Error creating payment intent:', error);
  }
};
export const directChatCreateStripePaymentIntent = (body, params = {}) => async (
  dispatch,
  getState,
  sdk
) => {
  dispatch(createPaymentIntentRequest());
  const currentUser = await dispatch(fetchCurrentUser());
  const currentUserId = currentUser?.id?.uuid;
  try {
    const { paymentIntentId } = await createChatPurchaseIntent({
      ...body,
      currentUserId,
    });
    dispatch(createPaymentIntentSuccess(paymentIntentId));

    const directMessageServices =
      currentUser?.attributes?.profile?.protectedData?.directMessageServices || {};

    const { listingId, itemName, priceAmount, navigateToOrderDetails } = params || {};

    // Check if already purchased
    if (directMessageServices[listingId]) {
      dispatch(createPaymentIntentFailure('Already purchased for this listing'));
      showErrorToast('You have already purchased direct chat for this listing');
      return;
    }

    const transactionBodyParams = {
      processAlias: TRANSACTION_PROCESS_ALIAS.DEFAULT_INQUIRY,
      transition: Transitions.INQUIRE,
      params: {
        listingId,
        protectedData: {
          paymentIntentId,
        },
      },
    };
    const { data: { data: transactionResponse = {} } = {} } = await sdk.transactions.initiate(
      transactionBodyParams
    );
    const transactionId = transactionResponse?.id?.uuid;

    if (paymentIntentId && transactionId) {
      const updatedServices = {
        ...directMessageServices,
        [listingId]: {
          itemName,
          priceAmount: parseFloat(priceAmount),
          // paymentIntentId,
          transactionId,
          purchaseDate: new Date().toISOString(),
        },
      };

      await dispatch(
        updateProfile({
          protectedData: { directMessageServices: updatedServices },
        })
      );
      navigateToOrderDetails(transactionId);
    }
  } catch (error) {
    dispatch(createPaymentIntentFailure(error.message));
  }
};
export const cancelListingSubscriptionAsync = params => async (dispatch, getState, sdk) => {
  dispatch(cancelListingSubscriptionRequest(params?.subscriptionId));
  try {
    const response = await cancelListingSubscription(params);
    if (response.statusCode === StatusCode.SUCCESS) {
      dispatch(fetchCurrentUser());
      dispatch(cancelListingSubscriptionSuccess());
      showSuccessToast('Successfully cancelled subscription');
    }
  } catch (error) {
    showErrorToast('Error while cancelling subscription');
    dispatch(cancelListingSubscriptionError(storableError(error)));
    throw new Error(error.message);
  }
};
